import axios, { AxiosPromise } from 'axios';
import _get from 'lodash/get';

import constants from './index';

import { setApiAuthenticationHeader } from 'utils/setApiAuthenticationHeader';
import store from 'store';
import authTypes from 'store/auth/types';
import { UserModel } from 'utils/types';

import { ExtendModal, ModifyModal } from 'store/authorization/extendAndModify/actions';
import { DownloadAttachment } from 'store/authorization/attach/actions';
import { GetSubmittedFileNames } from 'store/authorization/files/actions';
import { GetAuthList } from 'store/authorization/list/actions';
import { GetFaxes } from 'store/authorization/faxes/actions';
import { CaseManagementModel, SearchCriteria } from 'store/eligibility/actions';
import { AuthHistory } from 'store/authorization/history/actions';
import { ClaimHistory } from 'store/claim/actions';
import { LmpInfo } from 'store/eligibility/lmp/actions';
import { EddInfo } from 'store/eligibility/edd/actions';
import { CategoryModel, DocumentsMemberInfo, DownloadMemberDocument } from 'store/eligibility/documents/actions';
import { SaveTask } from 'store/eligibility/mcp/actions';
import { HmoAwvMetricsModel, HmoMemberAwvDetailsModel, MemberMpiSearchModel, MemberBatchUpdateModel, MemberExcelStatusModel, MemberPafModel, RefreshMemberAwvStatusModel } from 'store/app/hmo/actions';
import { UpdateStatusAndCommentModal } from 'store/eligibility/hcc/actions';
import { DownloadDocument } from 'store/eligibility/gap/actions';
import { STAGING_ROOT } from 'utils/objects';

const {
  API_SERVER,
  CENTRAL_API_SERVER,
  ROOT,
  UMS_CLIENT_ID,
  UMS_CLIENT_SECRET,
  RESOURCE_ROOT
} = constants;

// Configure axios instances
axios.defaults.baseURL = API_SERVER;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';

const axiosErrorHandler = (error: any) => {
  if (error.config && error.response && error.response.status === 401 || error.response.status === 403) {

    if (window.location.pathname !== '/login') {
      // TODO: fix redux store types
      // localStorage.clear();
      sessionStorage.clear(); // TODO: switch with localStorage
      setApiAuthenticationHeader();

      store.dispatch({
        type: authTypes.LOGOUT_SUCCESS,
        payload: {
          msg: 'Logout success!',
        },
      });

      window.location.href = '/login';
    }

    // Leave this here for now
    // access token expired, get new one
    // const refresh_token = store.getState().rootReducer.auth.refresh_token;
    // if (refresh_token) {
    //   return api.auth.getNewToken(refresh_token)
    //     .then(response => {
    //       const access_tokenNew = response?.access_token;
    //       const refresh_tokenNew = response?.refresh_token;

    //       if (access_tokenNew && refresh_tokenNew) {
    //         error.config.headers.common = {
    //           ...error.config.headers.common,
    //           AuthenticationToken: access_tokenNew,
    //         };
    //         error.config.headers.AuthenticationToken = access_tokenNew;
    //         setApiAuthenticationHeader(access_tokenNew);
    //         localStorage.access_token = access_tokenNew;
    //         localStorage.refresh_token = refresh_tokenNew;

    //         store.dispatch({
    //           type: authTypes.LOGIN_SUCCESS,
    //           payload: {
    //             Token: access_tokenNew,
    //             SessionId: refresh_tokenNew,
    //           },
    //         });

    //         return axiosInstance.request(error.config);
    //       } else {
    //         return Promise.reject(Error('Cannot get access token.'));
    //       }
    //   }).catch((error: any) => {
    //     // Could not find API token
    //     if (error.config && error.response && error.response.status === 404) {
    //       logout();
    //     }
    //   })
    // } else {
    //   return Promise.reject(Error('Refresh Token unavailable.'))
    // }
  }
  return Promise.reject(error);
};

//====================================================================================
// Create main axios instance
let axiosInstance = axios.create();

// axios interceptors to handle token expiration
axiosInstance.interceptors.response.use(
  response => response,
  axiosErrorHandler,
);

//====================================================================================
const multiPartConfig = {
  baseURL: API_SERVER,
  headers: {
    'Content-Type': 'multipart/form-data',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
};
const multiPartInstance = axios.create(multiPartConfig);

multiPartInstance.interceptors.response.use(
  response => response,
  axiosErrorHandler,
);

//====================================================================================
//Create UMS axios instance, UMS server accepts form-urlencoded only, so we need to create separate instance here
const umsConfig = {
  baseURL: API_SERVER,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'Accept': 'application/json, text/plain, */*',
    'X-Requested-With': 'XMLHttpRequest',
  },
};
const umsAxios = axios.create(umsConfig);

umsAxios.interceptors.response.use(
  response => response,
  axiosErrorHandler,
);

//====================================================================================
//Create Central axios instance
const centralConfig = {
  baseURL: CENTRAL_API_SERVER,
  headers: {
    'Accept': 'application/json, text/plain, */*',
    'X-Requested-With': 'XMLHttpRequest',
  },
};
const centralAxios = axios.create(centralConfig);

centralAxios.interceptors.response.use(
  response => response,
  axiosErrorHandler,
);

//====================================================================================
//Switch API SERVER axios instance
export const setApiServer = (ipaString: string) => {
  const serverKey = `${ipaString}_API_SERVER` as keyof typeof constants;
  const rootKey = `${ipaString}_ROOT` as keyof typeof constants;

  if (constants.hasOwnProperty(serverKey) && constants.hasOwnProperty(rootKey)) {
    const server = constants[serverKey];
    const root = constants[rootKey];

    axiosInstance.defaults.baseURL = server;
    umsAxios.defaults.baseURL = server;
    multiPartInstance.defaults.baseURL = server;

    sessionStorage.setItem('root', ipaString === 'STAGING' ? STAGING_ROOT : root);
  } else { // QA or AHC
    axiosInstance.defaults.baseURL = API_SERVER;
    umsAxios.defaults.baseURL = API_SERVER;
    multiPartInstance.defaults.baseURL = API_SERVER;

    sessionStorage.setItem('root', ROOT);
  }

  sessionStorage.setItem('RESOURCE_ROOT', RESOURCE_ROOT);
};

//====================================================================================

// Axios wrapper to handle error
const axiosWrapper = (apiCall: AxiosPromise) => apiCall.then(res => res.data).catch(err => Promise.reject(err));

// Define API calls
let api = {
  isFakeApi: false,

  // Application-wide APIs
  app: {
    createStatistic: (statistic: any) => axiosWrapper(axiosInstance.post('App/CreateStatistic', statistic)),
    getIpaByUser: (userId: string) => axiosWrapper(axiosInstance.get(`IpaByUser?userId=${userId}`)),
    getVendors: (provKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`GetVendors?provKeyId=${provKeyId}&companyId=${companyId}`)),
    getDistinctVendors: (provKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`GetVendorAccess?provKeyId=${provKeyId}&companyId=${companyId}`)), //active
    reportIssue: (form: any) => axiosWrapper(axiosInstance.post('App/Issue', form)),
    getContacts: () => axiosWrapper(axiosInstance.get(`App/ContactUs`)),
    getCompaniesInfo: () => axiosWrapper(axiosInstance.get(`App/CompanyName`)), //Active
    getDownloadableForms: () => axiosWrapper(axiosInstance.get(`App/DownloadableForms`)),
    getDownloadableResources: (materialType: string, measureCode: string, companyId: string) => axiosWrapper(axiosInstance.get(`App/GetDownloadableResources?companyId=${companyId}&materialType=${materialType}&measureCode=${measureCode}`)), //Active
    getDownload: (filename: string) => axiosWrapper(axiosInstance.get(`App/GetDownload?file=${filename}`, { responseType: "arraybuffer", headers: {
      Accept: 'application/pdf'
    } })), //Active
    getDownloadResources: (filename: string, companyId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/DownloadResources?file=${filename}&companyId=${companyId}`, { responseType: "arraybuffer", headers: {
      Accept: 'application/pdf'
    } })), //Active
    getUserAcknowledgement: (userId: string) => axiosWrapper(axiosInstance.get(`App/GetUserAcknowledgement?userId=${userId}`)), //Active
    getFaxOptOutExists: (userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetFaxOptOutExists?userId=${userId}`,)), //Active
    bulkInsertFaxOptOutProviders:(parameter: any) =>  axiosWrapper(axiosInstance.post(`MemberSearch/BulkInsertFaxOptOutProviders`, parameter)), //Active
    generateFaxOptReport: (userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GenerateFaxOptReport?userId=${userId}`, { responseType: 'blob' })), //Active
    createUserAcknowledgement: (userModel: any) => axiosWrapper(axiosInstance.post(`App/CreateUserAcknowledgement`, userModel)), //Active
    CheckAttestationVisibility: (userId: string, attestationName: string) => axiosWrapper(axiosInstance.get(`App/CheckAttestationVisibility?userId=${userId}&attestationName=${attestationName}`)), //Active
    AddAttestationAcknowledgement: (userModel: any) => axiosWrapper(axiosInstance.post(`App/AddAttestationAcknowledgement`, userModel)), //Active
    DownloadAttestation: (requestModel: any) => axiosWrapper(axiosInstance.post(`App/DownloadAttestation`, requestModel, {
      responseType: "arraybuffer",
      headers: {
        Accept: 'application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    })), //Active
    GetAllAttestationTimestamp: (userId: string) => axiosWrapper(axiosInstance.get(`App/GetAllAttestationTimestamp?userId=${userId}`)),//Active
    GetAttestationPopupContent: (attestationName: string) => axiosWrapper(axiosInstance.get(`App/GetAttestationPopupContent?attestationName=${attestationName}`)), //Active

    sendFeedback: (formObject: any) => axiosWrapper(axiosInstance.post(`App/AddUserFeedback`, formObject)), //active
    getMeasureReportPrintFile: (params: Object) => axiosWrapper(axiosInstance.post('GetMeasureReportPrintFile', params, {responseType: 'arraybuffer'})), //Active
    providerKeyListWDates: {
      get: (userId: string) => axiosWrapper(axiosInstance.get(`App/ProviderKeyListWDates?userId=${userId}`)), //active
      getDistinct: (userId: string) => axiosWrapper(axiosInstance.get(`App/DistinctProviderKeyListWDates?userId=${userId}`)), //active
    },

    userProfile: {
      update: (userModel: Object) => axiosWrapper(axiosInstance.post('App/UpdateProfile', userModel)),
    },

    ipa: {
      getContractedIPA: (userId: string) => axiosWrapper(axiosInstance.post('MemberSearch/ContractedIPA', {userId: userId})),
      getAll: (userId: string) => axiosWrapper(axiosInstance.get(`App/GetAllIpas?userId=${userId}`)),
      getAllDesc: () => axiosWrapper(axiosInstance.get(`App/GetAllIpasDesc`)), //Active
      getEzcapIpasDesc: () => axiosWrapper(axiosInstance.get(`App/GetEzcapIpasDesc`)), //Active
      getResources: (userId: string, materialType: string, measureCode: string) => axiosWrapper(axiosInstance.get(`App/GetIPAResources?userId=${userId}&materialType=${materialType}&measureCode=${measureCode}`)),
      deleteResource: (formObject: any) => axiosWrapper(axiosInstance.post('MemberSearch/DeleteResources', formObject)), //Active
      updateResource: (formObject: any) => axiosWrapper(axiosInstance.post('MemberSearch/UpdateResources', formObject)),
      uploadResource: (formObject: any) => axiosWrapper(axiosInstance.post('MemberSearch/UploadResources', formObject)),
      uploadProviderResources: (formObject: any) => axiosWrapper(axiosInstance.post('MemberSearch/UploadProviderResources', formObject)),
      searchSpecialtyName: (formObject: any) => axiosWrapper(axiosInstance.post('MemberSearch/SearchSpecialtyName', formObject)),
      searchProviderByName: (formObject: any) => axiosWrapper(axiosInstance.post('MemberSearch/SearchProviderByName', formObject)),
      deleteProviderResources: (formObject: any) => axiosWrapper(axiosInstance.post('MemberSearch/DeleteProviderResources', formObject)),
      updateProviderResources: (formObject: any) => axiosWrapper(axiosInstance.post('MemberSearch/UpdateProviderResources', formObject)),
    },

    portal: {
      getVersion: () => axiosWrapper(axiosInstance.get(`App/GetPortalVersion`)), //inactive now using package.json version
      getContact: () => axiosWrapper(axiosInstance.get(`App/PortalContact`)), //Active
    },

    memberReport: {
      printMembersReport: (parameter: any) => axiosWrapper(axiosInstance.post('GetMemberReportPrintFile', parameter, {responseType: 'arraybuffer'})), //Active
      downloadMemberFile: (parameter: any) => axiosWrapper(axiosInstance.post('GetMemberReportExcelFile', parameter, {responseType: 'arraybuffer'})), //Active
    },

    banner: {
      getActiveBannerDetails: (companyId: string) => axiosWrapper(axiosInstance.get(`App/GetActiveBannerDetails?companyId=${companyId}`)),
    },

    helpCenter: {
      getHelpCenterLinks: (companyId: string, page: string, type: string) => axiosWrapper(axiosInstance.get(`App/GetHelpCenterLinks?companyId=${companyId}&page=${page}&type=${type}`)),
    }
  },

  // Parameter APIs
  parameter: {
    getFileTypes: (parameter: any) => axiosWrapper(axiosInstance.get(`Parameter?parameter=${parameter}`)), //integrated
    getServer: (companyId: string) => axiosWrapper(axiosInstance.get(`ServerParams?companyId=${companyId}`)),
  },

  // Authentication APIs
  auth: {
    login: (credentials: any) => axiosWrapper(umsAxios.post('Login', credentials)), // connected
    logout: () => axiosWrapper(umsAxios.get('Login')), // unused
    getNewToken: (refreshToken: any) => {
      const body = `grant_type=refresh_token`
        + `&refresh_token=${refreshToken}`
        + `&scope=api1%20offline_access%20`
        + `&client_id=${UMS_CLIENT_ID}`
        + `&client_secret=${UMS_CLIENT_SECRET}`;

      return axiosWrapper(umsAxios.post('connect/token', body)); // invalid
    },
    check: () => axiosWrapper(axiosInstance.get('/user/check')), // invalid
    getMySelf: () => axiosWrapper(axiosInstance.get('/users/myself')), // invalid

    sendRegistration: (form: Object) => axiosWrapper(axiosInstance.post('App/SendRegisteration', form)),

    sendRegistrationToCentral: (form: Object) => axiosWrapper(centralAxios.post('register', form)),
    checkCurrentPassword: (pwd: string, userId: string) => axiosWrapper(axiosInstance.post('App/CheckCurrentPassword', {Password: pwd, UserId: userId})),
    checkPasswordHistory: (uid: string, pwd: string) => axiosWrapper(axiosInstance.post('App/PasswordHistoryCheck', {Password: pwd, UserId: uid})),
    updatePassword: (input: string, userId: string) => axiosWrapper(axiosInstance.post('App/UpdatePassword', {Password: input, UserId: userId})),
    resetPassword: (username: string, code: string, password: string) => axiosWrapper(axiosInstance.post('App/ResetPassword', {Password: password, Username: username, Code: code})),
    forgotPassword: (username: string, portalName: string) => axiosWrapper(axiosInstance.post(`v2/Login/ForgotPassword?Username=${username}&IpaGroup=${portalName}`)),
    updateLastLoginDate: (userId: string) => axiosWrapper(axiosInstance.get(`Login/UpdateLastLoginDate?userId=${userId}`)),

  },

  // Authorization APIs
  authorization: {
    member: {
      get: (searchReq: any) => axiosWrapper(axiosInstance.post('AuthMember/GetAuthMember', searchReq)), //integrated
      priorAuthValidation: (searchReq: any) => axiosWrapper(axiosInstance.post('AuthMember/PriorAuthValidation', searchReq)), //integrated
      updateAddressAndPhone: (memberUpdateObj: any) => axiosWrapper(axiosInstance.post('AuthMember/UpdateMemberAddressAndPhoneInfo', memberUpdateObj)),
    },
    list: {
      get: (userModel: GetAuthList) => axiosWrapper(axiosInstance.post('AuthMember/GetAuthList', userModel)), //Active
      getGroups: (userModel: any) => axiosWrapper(axiosInstance.post('AuthMember/GetAuthListGroups', userModel)),

    },
    provider: {
      getProviderWithVendorInfo: (rmiProvKeyId: any) => axiosWrapper(axiosInstance.post(`AuthMember/GetProviderVendor?providerKeyId=${rmiProvKeyId}`)),
      getProviderName: (providerKeyId: any, companyId: any) => axiosWrapper(axiosInstance.get(`AuthProvider/Provider?providerKeyId=${providerKeyId}&companyId=${companyId}`)),
      isPcp: (providerKeyId: any, companyId: any) => axiosWrapper(axiosInstance.get(`AuthProvider/GetIsPcp?providerKeyId=${providerKeyId}&companyId=${companyId}`)),
      isArcadia: (providerKeyId: any, companyId: any) => axiosWrapper(axiosInstance.get(`AuthProvider/IsArcadia?providerKeyId=${providerKeyId}&companyId=${companyId}`)),
      getIpaAdvise: (companyId: any) => axiosWrapper(axiosInstance.get(`AuthProvider/GetIpaAdvise?companyId=${companyId}`)),
      getProviderHccMembersLob: (providerKeyId: string, lob: string, userId: string, companyId: string) => axiosWrapper(axiosInstance.get(`AuthProvider/ProviderHccMembersLob?provKey=${providerKeyId}&companyId=${companyId}&lob=${lob}&userId=${userId}`)), //Active
      getAuthSearchProviderList: (userModel: any) => axiosWrapper(axiosInstance.post('AuthMember/GetAuthSearchProviderList', userModel)), //integrated
      getProviderByCity: (searchObject: any) => axiosWrapper(axiosInstance.post('AuthMember/GetProviderList', searchObject)),
      getProviderByName: (searchObject: any) => axiosWrapper(axiosInstance.post('AuthMember/GetProviderByName', searchObject)),
      getAuthEnabledProviderList: (userModel: UserModel) => axiosWrapper(axiosInstance.post('AuthMember/GetAuthEnabledProviderList', userModel)), //Active
      acknowledgement: (ackTypes: any, membKeyId: string, companyId: string) => axiosWrapper(axiosInstance.post('AuthMember/ProviderAcknowledgement', {AcknowledgementTypes: ackTypes, MembKeyId: membKeyId, CompanyId: companyId})),
      checkInitialFollowUp: (companyId: string, membKeyId: string, provKeyId: string) => axiosWrapper(axiosInstance.get(`AuthMember/CheckInitialFollowUp?companyId=${companyId}&membKeyId=${membKeyId}&provKeyId=${provKeyId}`)),
      getAllIpaProviders: (companyId: string, userId: string) => axiosWrapper(axiosInstance.get(`AuthMember/IPAProviders?coId=${companyId}&userId=${userId}`)),
      searchAllByName: (val: string, userId: string, companyId: string) => axiosWrapper(axiosInstance.get(`AuthProvider/SearchAllProvider?val=${val}&userId=${userId}&companyId=${companyId}`)),
      getCECProviders: (userId: string, companyId: string) => axiosWrapper(axiosInstance.get(`AuthProvider/GetCECProvider?userId=${userId}&companyId=${companyId}`)), // Active
      getProviderTermDate: (userId: string, provKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`App/GetProviderTermDate?userId=${userId}&provKeyId=${provKeyId}&companyId=${companyId}`)),
      getEzCapProviderDetails: (provKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`AuthMember/GetEzCapProviderDetails?companyId=${companyId}&provKeyId=${provKeyId}`)),
      checkProviderForAuthSubmission: (provId: string, provKeyId: string, companyId: string, posCode: string, specCode: string) => axiosWrapper(axiosInstance.get(`AuthMember/CheckProviderForAuthSubmission?provId=${provId}&provKeyId=${provKeyId}&companyId=${companyId}&posCode=${posCode}&specCode=${specCode}`)),
    },
    history: {
      post: (member: AuthHistory) => axiosWrapper(axiosInstance.post('AuthHistory', member)), //Active
      getSpecCodeList: (providerKeyId: any, companyId: any, specialGroupId: any) => axiosWrapper(axiosInstance.get(`AuthHistory?providerKeyId=${providerKeyId}&companyId=${companyId}&specialGroupId=${specialGroupId}`)) //Active

    },
    specialists: {
      checkHistory:(membKeyId: any, provKeyId: any) => axiosWrapper(axiosInstance.get(`AuthMember/CheckSpecialistAuthHistory?rmiMembKeyId=${membKeyId}&rmiReqProvKeyId=${provKeyId}`)),
      validateOPTOPH:(model: any) => axiosWrapper(axiosInstance.post(`AuthMember/ValidateOPTOPH`, model)),
    },
    referType: {
      get: (dos: any) => axiosWrapper(axiosInstance.get(`AuthMember?dateOfService=${dos}`)),

    },
    tempAuthNumber: {
      get: (companyId: any) => axiosWrapper(axiosInstance.get(`AuthMember/TempAuthNumber?companyId=${companyId}`)),
      check: (tempAuthNumber: any) => axiosWrapper(axiosInstance.get(`AuthMember/TempAuthNumber?${tempAuthNumber}`)),

    },
    modifierCode: {
      get: () => axiosWrapper(axiosInstance.get('AuthMember/ModifierCodes')), //Active

    },
    pos: {
      get: (companyId: any) => axiosWrapper(axiosInstance.get(`AuthMember/POS?companyId=${companyId}`)),

    },
    expiryDays: {
      get: (companyId: string, lobCategory: string) => axiosWrapper(axiosInstance.get(`AuthMember/ExpiryDays?companyId=${companyId}&lobCategory=${lobCategory}`)),
    },
    diagRecords: {
      get: (authNo: any, companyId: any) => axiosWrapper(axiosInstance.get(`AuthMember/GetAuthDiagRecords?authNo=${authNo}&companyId=${companyId}`)),

    },
    diagCodes: {
      get: (search: any) => axiosWrapper(axiosInstance.post('AuthMember/Diagnosis', search)),

    },
    attach: {
      get: (authNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`AuthMember/GetAuthAttach?authNo=${authNo}&companyId=${companyId}`)), //Active
      getInfo: (authNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`AuthMember/GetAuthAttachInfo?authNo=${authNo}&companyId=${companyId}`)), //Active
      download: (model: DownloadAttachment) => axiosWrapper(axiosInstance.post('AuthMember/Attachment', model, { responseType: 'blob' })), //Active
      isRequired: () => axiosWrapper(axiosInstance.get(`AuthMember/IsAttachmentRequired`)),
    },
    portal: {
      get: (authNo: string, companyId: string, userRole: number) => axiosWrapper(axiosInstance.get(`AuthMember/GetPortalAuth?authNo=${authNo}&companyId=${companyId}&userRole=${userRole}`)), //Active

    },
    extendAndModify: {
      getAllow: (companyId: any) => axiosWrapper(axiosInstance.get(`AuthMember/AllowExtendAndModifyAuth?companyId=${companyId}`)),
      extend: (extendModel: ExtendModal) => axiosWrapper(axiosInstance.post('AuthMember/ExtendAuth', extendModel)), //Active
      modify: (model: ModifyModal) => axiosWrapper(axiosInstance.post('AuthMember/ModifyAuth', model)), //Active
      cancel: (authNo: string, companyId: string, cancelReason: string) => axiosWrapper(axiosInstance.post(`AuthMember/CancelAuth?authNo=${authNo}&companyId=${companyId}&cancelReason=${cancelReason}`)), //Active
      expire: (request: any) => axiosWrapper(axiosInstance.post('AuthMember/ExpireAuth', request)), //Active
    },
    files: {
      upload: (options: Object) => axiosWrapper(axiosInstance.post('AuthMember/AuthCreateUploadFiles', options)), //Active
      getSubmittedFileNames: (model: GetSubmittedFileNames) => axiosWrapper(axiosInstance.post('AuthMember/GetSubmittedFileNames', model)), //Active
      getAllFilesByTags: (request: Object) => axiosWrapper(axiosInstance.post('MemberSearch/GetAllFilesByTagsFromBlob', request)),
      download: (request: Object) => axiosWrapper(axiosInstance.post('MemberSearch/DownloadFileFromBlob', request, { responseType: 'blob' })),
    },
    facilities: {
      get: (search: any) => axiosWrapper(axiosInstance.post('AuthMember/GetFacilities', search)),
      getClaimsFacilities: (search: any) => axiosWrapper(axiosInstance.post('GetClaimsFacilities', search)),

    },
    city: {
      getList: (searchObject: any) => axiosWrapper(axiosInstance.post('AuthMember/GetCityList', searchObject)),

    },
    PMTProv: {
      get: (payload: any) => axiosWrapper(axiosInstance.post('AuthMember/GetPMTProv', payload)), // Active
      postPainManagement: (payload: any) => axiosWrapper(axiosInstance.post('AuthMember/PainManagement', payload)), // Active
      validatePulmNoduleAndPainClinic: (payload: any) => axiosWrapper(axiosInstance.post('AuthMember/ValidatePulmNoduleAndPainClinic', payload)), // Active
    },
    duplicateAuth: {
      validateDuplicateAuth: (payload: any) => axiosWrapper(axiosInstance.post('AuthMember/validateDuplicateAuth', payload)), // Active
    },
    enquireService: {
      post: (searchCriteria: any) => axiosWrapper(axiosInstance.post('AuthMember/EnquireService', searchCriteria)), // Active

    },
    OBCodes: {
      get: (companyId: any, svcCode: any) => axiosWrapper(axiosInstance.post(`AuthMember/CptCompanionCodes?companyId=${companyId}&specCode=OB&svcCode=${svcCode}`)),

    },
    mediCal: {
      checkRestrict: (cptCode: any) => axiosWrapper(axiosInstance.post(`AuthMember/MediCalBenefitRestrict?cptCode=${cptCode}`)), //Active
      checkCode: (hpCode: any) => axiosWrapper(axiosInstance.post(`AuthMember/MediCalCode?hpCode=${hpCode}`)),

    },
    submitConfirmedAuth: {
      post: (authModel: any) => axiosWrapper(axiosInstance.post('AuthMember/AuthCreateSubmitData', authModel)),

    },
    letter: {
      getInfo: (authNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`AuthMember/GetAuthLetterInfo?authNo=${authNo}&companyId=${companyId}`)), //Active
      letterView: (userId: string) => axiosWrapper(axiosInstance.post('AuthMember/LetterView', userId)),
    },
    cpsp: {
      post: (cpspModel: any) => axiosWrapper(axiosInstance.post('AuthMember/CPSP', cpspModel)),
    },
    faxes: {
      get: (params: GetFaxes) => axiosWrapper(axiosInstance.post('AuthMember/GetFaxes', params)),
      getStatus: () => axiosWrapper(axiosInstance.get('AuthMember/FaxStatus')),
    },
    ruleCodes: {
      get: (setName: string) => axiosWrapper(axiosInstance.get(`AuthMember/RuleCodes?SetName=${setName}`)),
    },

    survey: {
      check: (surveyId: string) => axiosWrapper(axiosInstance.get(`AuthMember/CheckSurvey?surveyId=${surveyId}`)),
      getQuestions: (surveyId: string) => axiosWrapper(axiosInstance.get(`AuthMember/SurveyQuestion?surveyId=${surveyId}`)),
      save: (model: any) => axiosWrapper(axiosInstance.post(`AuthMember/SaveSurvey`, model)),
    },

    expired: {
      get: (userId: string) => axiosWrapper(axiosInstance.post(`AuthProvider/GetExpiredAuths?userId=${userId}`)),
    },

    unused: {
      get: (userId: string, rptType: string, provKeyId: string) => axiosWrapper(axiosInstance.post(`AuthProvider/GetUnusedAuths?userId=${userId}&rptType=${rptType}&provKeyId=${provKeyId}`)),
    },

    hcc: {
      getMemberMeasures: (memberMpiNo: string, provKey: string, companyId: string, lob: string, userId: string) => axiosWrapper(axiosInstance.get(`AuthProvider/GetMemberMeasures?memberMpiNo=${memberMpiNo}&provKey=${provKey}&companyId=${companyId}&lob=${lob}&userId=${userId}`)), //Active
      addMemberDocsToHccDocs: (hccIdAndMemberDocsObj: Object) => axiosWrapper(axiosInstance.post(`AuthProvider/AddHccDocs`, hccIdAndMemberDocsObj)),
      printProviderMemberReport: (materials: Object) => axiosWrapper(axiosInstance.post(`GetProviderHccMemberDetailPrintFile`, materials, {responseType: 'arraybuffer'})), //Active
    }
  },

  claim: {
    get: (claimNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`Claim?claimNo=${claimNo}&companyId=${companyId}`)), // active
    getSubmitted: (userId: string) => axiosWrapper(axiosInstance.get(`SubmittedClaims?userId=${userId}`)), // active
    getSubmittedUsingEdifecs: (searchObject: Object) => axiosWrapper(axiosInstance.post(`SubmittedClaimsUsingEdifecs`, searchObject)), // active
    getSubmissionFile: (submissionId: string, fileType: string) => axiosWrapper(axiosInstance.get(`GetClaimSubmissionFile?strSubmissionId=${submissionId}&fileType=${fileType}`)),
    getSubmissionFileUsingEdifecs: (submissionId: string, fileType: string, refNum: string, ipa: string) => axiosWrapper(axiosInstance.get(`GetSubmissionFileUsingEdifecs?strSubmissionId=${submissionId}&fileType=${fileType}&refNum=${refNum}&ipa=${ipa}`, {responseType: 'arraybuffer'})),
    search: (searchObject: Object) => axiosWrapper(axiosInstance.post(`Claims`, searchObject)), // active
    create: (companyId: string, membKeyId: string, provKeyId: string, userId: string, hpCode: string, startDate: string) => axiosWrapper(axiosInstance.get(`CreateClaim?companyId=${companyId}&membKeyId=${membKeyId}&provKeyId=${provKeyId}&userId=${userId}&hpCode=${hpCode}&startDate=${startDate}`)), // active
    save: (claim: any) => axiosWrapper(axiosInstance.post(`SaveClaim`, claim)), // active
    getEZ: (claimNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`EzClaim?claimNo=${claimNo}&companyId=${companyId}`)), // active
    cancel: (cancelClaimData: Object) => axiosWrapper(axiosInstance.post(`CancelClaim`, cancelClaimData)),
    postHistory: (member: ClaimHistory) => axiosWrapper(axiosInstance.post(`ClaimHistory`, member)), //Active
    uploadAttachment: (uploadObject: Object) => axiosWrapper(multiPartInstance.post(`ClaimMember/UploadFiles`, uploadObject)), // active
    getEzcapClaimStatus: (companyId: string, claimNo: string) => axiosWrapper(axiosInstance.get(`GetEzcapClaimStatus?ipa=${companyId}&claimNo=${claimNo}`)), // active
    GetInvalidPrimDxInitials: (companyId: string) => axiosWrapper(axiosInstance.get(`GetInvalidPrimDxInitials?ipa=${companyId}`)), // active

    portal: {
      get: (claimNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`PortalClaim?claimNo=${claimNo}&companyId=${companyId}`)), // active
      getUsingEdifecs: (claimNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`PortalClaimUsingEdifecs?claimNo=${claimNo}&companyId=${companyId}`)), // active
    },

    npi: {
      getProviderNPI: (provKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`GetProviderNPI?provKeyId=${provKeyId}&companyId=${companyId}`)), // active
      getOther: (provKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`GetOtherProviderNPI?provKeyId=${provKeyId}&companyId=${companyId}`)),
      checkIsDME: (provKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`CheckIsDME?provKeyId=${provKeyId}&companyId=${companyId}`)), // active
      validateRendering: (npi: string, provKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`ValidateRenderingNPI?npi=${npi}&provKeyId=${provKeyId}&companyId=${companyId}`)), // active
      verify: (npi: string) => axiosWrapper(axiosInstance.get(`VerifyNPI?npi=${npi}`)), // active
      getName: (npi: string) => axiosWrapper(axiosInstance.get(`NameFromNpi?npi=${npi}`)), // active
      getRmiProvKeyId: (npi: string, companyId: string) => axiosWrapper(axiosInstance.get(`GetRmiProvKeyIdFromRendNpi?npi=${npi}&companyId=${companyId}`)),
      getNameAndIdFromRmiProvKeyId: (rmiProvKeyId: string) => axiosWrapper(axiosInstance.get(`GetNameAndIdFromRmiProvKeyId?rmiProvKeyId=${rmiProvKeyId}`)),

    },

    providerList: {
      getClaimRead: (userId: string) => axiosWrapper(axiosInstance.get(`ClaimReadProviderList?userId=${userId}`)), // active
      getClaimReadWrite: (userId: string) => axiosWrapper(axiosInstance.get(`ClaimReadWriteProviderList?userId=${userId}`)),
    },

    member: {
      search: (search: Object) => axiosWrapper(axiosInstance.post(`ClaimMembers`, search)), // active
      getAttachments: (claimNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`ClaimMember/GetClaimAttachments?claimNo=${claimNo}&companyId=${companyId}`)), // active
      getAuthDetail: (membKeyId: string, provKeyId: string, authNo: string) => axiosWrapper(axiosInstance.get(`GetMemberAuthDetail?membKeyId=${membKeyId}&provKeyId=${provKeyId}&authNo=${authNo}`)),
    },

    cptCodes: {
      searchByType: (userId: string, types: any) => axiosWrapper(axiosInstance.get(`SearchCPTCodeByTypes?userId=${userId}&types=${types}`)), // active
      get: (searchCriteria: Object) => axiosWrapper(axiosInstance.post(`EnquireService`, searchCriteria)), // active
    },

    ndc: {
      checkCPTCompatibility: (cpt: any, ndc: any) => axiosWrapper(axiosInstance.get(`CheckNdcCptCompatibility?cpt=${cpt}&ndc=${ndc}`)), // active
      checkBulkCPTCompability: (array: any) => axiosWrapper(axiosInstance.post(`CheckBulkNdcCptCompatibility`, array)),
      getRelated: (cpt: any) => axiosWrapper(axiosInstance.get(`RelatedNdc?cpt=${cpt}`)), // active
      getNdcCodes: (code: any) => axiosWrapper(axiosInstance.get(`GetNdcCodes?code=${code}`)),  // active
      checkIfNdcIsRequired: (cpt: any) => axiosWrapper(axiosInstance.get(`IsNdcRequired?cpt=${cpt}`)), //active
    },

    batchReports: {
      upload: (uploadData: Object) => axiosWrapper(multiPartInstance.post(`UploadBatchClaimTestFile`, uploadData)), // active
      uploadBatchClaimFiles: (uploadData: Object) => axiosWrapper(multiPartInstance.post(`UploadBatchClaimFiles`, uploadData)), // active
      getSubmitted: (userId: string) => axiosWrapper(axiosInstance.get(`SubmittedBatchReports?userId=${userId}`)), // active
      getSubmittedUsingEdifecs: (searchObject: Object) => axiosWrapper(axiosInstance.post(`SubmittedBatchReportsUsingEdifecs`, searchObject)), // active
      getTestHistory: (userId: string) => axiosWrapper(axiosInstance.get(`GetClaimBatchTestHist?userId=${userId}`)), //active
      getAllTestHistory: (userId: string) => axiosWrapper(axiosInstance.get(`GetAllClaimBatchTestHist?userId=${userId}`)), //active
      insertHeader: (claimSubmission: any) => axiosWrapper(axiosInstance.post(`InsertClaimBatchHeader`, claimSubmission)),
    },
  },

  eligibility: {
    enquireBulkDiags: (searchCriteria: any) => axiosWrapper(axiosInstance.post('MemberSearch/EnquireBulkDiags', searchCriteria)),
    enquireBulkCpts: (searchCriteria: any) => axiosWrapper(axiosInstance.post('MemberSearch/EnquireBulkCpts', searchCriteria)),

    search: (searchCriteria: SearchCriteria) => axiosWrapper(axiosInstance.post('MemberSearch/Search', searchCriteria)), //Active
    getCMWorkList: (searchModel: CaseManagementModel) => axiosWrapper(axiosInstance.post('MemberSearch/MembCMWorkList', searchModel)), //Active
    getMember: (val: any, userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/Member?val=${val}&userId=${userId}`)), //Active
    getMemberRequest: (userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/MemberRequest?userId=${userId}`)), //Active
    getTaskOwners: (val: any, userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/Owner?val=${val}&userId=${userId}`)), //Active
    getProvider: (providerKeyId: string, companyId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/Details?providerKeyId=${providerKeyId}&companyId=${companyId}`)), //Active updated the route to MemberSearch/Details
    requestMember: (request: Object) => axiosWrapper(axiosInstance.post('MemberSearch/RequestMember', request)), //Active
    emailUpdatedMemberInfo: (memberInfo: any) => axiosWrapper(axiosInstance.post('MemberSearch/EmailUpdatedMemberInfo', memberInfo)),
    getMemberConditionCodes: (model: any) => axiosWrapper(axiosInstance.post('MemberSearch/ConditionCode', model)), //Active
    validateMeasures: (model: any) => axiosWrapper(axiosInstance.post('MemberSearch/ValidateMemberMeasures', model)), //Active

    claimGenerator: {
      submitMemberMeasures: (payload: any) => axiosWrapper(axiosInstance.post('MemberSearch/SubmitMemberMeasures', payload)), //Active
      saveHedisClaim: (claim: any) => axiosWrapper(axiosInstance.post('SaveHedisClaim', claim)), //Active
      updateMemberMeasureForClaimSubmission: (updateModel: Object) => axiosWrapper(axiosInstance.post('MemberSearch/SaveMemberMeasures', updateModel)), //Active
      validateHedisClaim: (claimModel: Object) => axiosWrapper(axiosInstance.post('ValidateHedisClaim', claimModel)), //Active
    },

    copay: {
      getInfo: (companyId: string, hpCode: string, optCode: string) => axiosWrapper(axiosInstance.post('MemberSearch/GetCopayInfo', {"CompanyId": companyId, "HpCode": hpCode, "OptCode": optCode})),
    },

    lmp: {
      getDate: (companyId: string, mpiNo: string) => axiosWrapper(axiosInstance.post('MemberSearch/GetLmpDate', {CompanyId: companyId, MembMpiNo: mpiNo})),
      saveDate: (details: LmpInfo) => axiosWrapper(axiosInstance.post(`MemberSearch/SaveLmpDate`, details)), //Active
    },

    edd: {
      getDate: (companyId: string, mpiNo: string) => axiosWrapper(axiosInstance.post('MemberSearch/GetEddDate', {CompanyId: companyId, MembMpiNo: mpiNo})),
      saveDate: (details: EddInfo) => axiosWrapper(axiosInstance.post(`MemberSearch/SaveEddDate`, details)), //Active
    },

    rafScore: {
      get: (providerKeyId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/RafScoreDetails?providerKeyId=${providerKeyId}`)), //Active updated the route to MemberSearch/RafScoreDetails
      getList: (userModel: any) => axiosWrapper(axiosInstance.post('MemberSearch/RafScoreList', userModel)), //Active
      getFile: (providerKeyId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/RafScoreFile?providerKeyId=${providerKeyId}`, {responseType: 'arraybuffer'})), //Active
      getHistoricalRafScore: (provId: string, companyId: string, userId: string, measureYear: number, vendorId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/HistoricalRafScore?provId=${provId}&companyId=${companyId}&userId=${userId}&measureYear=${measureYear}&vendorId=${vendorId}`)), //Active
    },

    health: {
      save: (memberHealth: any) => axiosWrapper(axiosInstance.post(`MemberSearch/SaveHealth`, memberHealth)),
    },

    measures: {
      get: (searchModel: any) => axiosWrapper(axiosInstance.post(`MemberSearch/GetMeasures`, searchModel)),
      getForMember: (searchModel: any) => axiosWrapper(axiosInstance.post(`MemberSearch/GetMemberMeasures`, searchModel)), //Active for claim generator
      getMembers: (measureCode: string, provKey: string, companyId: string, lob: string, pageIndex: number, pageSize: number, measureYear: number, vendorId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetMeasureMembers?measureCode=${measureCode}&provKey=${provKey}&companyId=${companyId}&lob=${lob}&pageIndex=${pageIndex}&pageSize=${pageSize}&measureYear=${measureYear}&vendorId=${vendorId}`)), //Active
      getPrintFile: (materials: Object) => axiosWrapper(axiosInstance.post(`GetMemberMeasurePrintFile`, materials, {responseType: 'arraybuffer'})), //API returns the file in binary //Active
      getProviderOverallPerformance: (model: Object) => axiosWrapper(axiosInstance.post('MemberSearch/GetProviderOverallPerformance', model)), //Active
      getHedisMeasures: (model: Object) => axiosWrapper(axiosInstance.post('measure/getHedisMeasures', model)), //Active
    },

    documents: {
      get: (memberInfo: DocumentsMemberInfo) => axiosWrapper(axiosInstance.post(`MemberSearch/GetMemberDocuments`, memberInfo)), //Active
      getCategory: (model: CategoryModel) => axiosWrapper(axiosInstance.post(`MemberSearch/GetDocumentCategory`, model)), //Active
      getCategoryDocs: (membDocPageRequestModel: any) => axiosWrapper(axiosInstance.post(`MemberSearch/GetCategoryDocsForMember`, membDocPageRequestModel)),
      getCategoryDocsForMemDocPage: (membDocPageRequestModel: any) => axiosWrapper(axiosInstance.post(`MemberSearch/CategoryDocsForMemDocPage`, membDocPageRequestModel)), //Active
      getGroupsAndCategories: (userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetDocumentGroupsAndCategories?userId=${userId}`)), //Active
      updateCategory: (document: any) => axiosWrapper(axiosInstance.post(`MemberSearch/UpdateDocumentCategory`, document)), //Active
      delete: (document: any) => axiosWrapper(axiosInstance.post(`MemberSearch/DeleteMemberDocument`, document)), //Active
      download: (model: DownloadMemberDocument) => axiosWrapper(axiosInstance.post(`MemberSearch/DownloadMemberDocument`, model, {responseType: 'arraybuffer'})), //Active
      getMostRecentDocsForMemDocPage: (params: any) => axiosWrapper(axiosInstance.post(`MemberSearch/GetMostRecentDocsForMemDocPage`, params)),
      getProviderByFileId: (userId: string, fileId: number) => axiosWrapper(axiosInstance.get(`MemberSearch/GetProviderByFileId?userId=${userId}&fileId=${fileId}`)),
      getSpecByFileId: (userId: string, fileId: number) => axiosWrapper(axiosInstance.get(`MemberSearch/GetSpecByFileId?userId=${userId}&fileId=${fileId}`)),
    },

    noteDocs: {
      get: (noteId: number) => axiosWrapper(axiosInstance.get(`MemberSearch/GetNoteDocs?noteId=${noteId}`)), //Active
      delete: (memberDocId: string) => axiosWrapper(axiosInstance.post(`MemberSearch/DeleteNoteDoc?memberDocId=${memberDocId}`, memberDocId)), //Active
    },

    carePlan: {
      getDocuments: (userId: string) => axiosWrapper(axiosInstance.post(`MemberSearch/GetCarePlanDocuments?userId=${userId}`)), //Active
      getNewItemsCount: (userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/NewCarePlanItemsCount?userId=${userId}`)),
    },

    gap: {
      getProviderReport: (provKey: string, companyId: string, userId: string, rptType: string, measureYear: number, vendorId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetProviderGapReport?userId=${userId}&prov=${provKey}&coId=${companyId}&rptType=${rptType}&measureYear=${measureYear}&vendorId=${vendorId}`)), //Active
      getProviderHedis: (provKey: string, companyId: string, lob: string, measureYear: number, vendorId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetProviderHedisGap?provKey=${provKey}&companyId=${companyId}&lob=${lob}&measureYear=${measureYear}&vendorId=${vendorId}`)), //Active
      getLastRunDate: (provKey: string, companyId: string, reportType: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetLastGapRunDate?provKey=${provKey}&companyId=${companyId}&reportType=${reportType}`)), //Active
      download: (model: DownloadDocument) => axiosWrapper(axiosInstance.post('MemberSearch/DownloadDocument', model, {responseType: 'arraybuffer'})), //Active
    },

    immunization: {
      get: (mpi: string) => axiosWrapper(axiosInstance.post(`MemberSearch/GetImmunization?membMpiNo=${mpi}`)), //Active
    },

    hcc: {
      getDiags: (companyId: string, membMpiNo: string, measureYear: number) => axiosWrapper(axiosInstance.get(`MemberSearch/GetMemberHccAndDiags?companyId=${companyId}&membMpi=${membMpiNo}&measureYear=${measureYear}`)), //Active
      getDetails: (companyId: string, membMpi: string, hccCode: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetHccDetail?companyId=${companyId}&membMpi=${membMpi}&hccCode=${hccCode}`)), //Active update route to MemberSearch/GetHccDetail
      saveDiagsFromClaim: (diagUpdateObj: any) => axiosWrapper(axiosInstance.post(`MemberSearch/SaveHccDiagsFromClaim`, diagUpdateObj)),
      updateStatusAndComment: (model: UpdateStatusAndCommentModal) => axiosWrapper(axiosInstance.post(`MemberSearch/UpdateHccStatusAndComment`, model)), //Active
      delete: (memberDocId: string) => axiosWrapper(axiosInstance.post(`MemberSearch/DeleteHccDoc?memberDocId=${memberDocId}`, memberDocId)), // rmi had a stroke here lmao //Active

    },

    encounter: {
      get: (details: any) => axiosWrapper(axiosInstance.post(`MemberSearch/GetEncounter`, details)), //Active
      save: (details: any) => axiosWrapper(axiosInstance.post(`MemberSearch/SaveEncounter`, details)), //Active
    },

    pac: {
      getNotes: (userId: string, membMpiNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/PacNotes?id=${membMpiNo}&companyId=${companyId}&userId=${userId}`)),
      getInfoForCarePlanPage: (userId: string, pacType: string) => axiosWrapper(axiosInstance.get(`MemberSearch/PacInfoForCarePlanPage?userId=${userId}&pacType=${pacType}`)), //Active
      getUnreadTypeCount: (userId: string, pacType: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetUnreadPacTypeCount?userId=${userId}&pacType=${pacType}`)), //Active
      getNoteLogs: (noteId: number) => axiosWrapper(axiosInstance.get(`MemberSearch/NoteLogs?noteId=${noteId}`)), //Active
      getStatus: () => axiosWrapper(axiosInstance.get(`MemberSearch/PacStatus`)),
      getPlans: (userId: string, membMpiNo: string, companyId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/PacPlans?id=${membMpiNo}&companyId=${companyId}&userId=${userId}`)), //Active
      getDetailPlan: (planId: number, userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/PacDetailPlan?id=${planId}&userId=${userId}`)), //Active
      getMedDetail: (planId: number) => axiosWrapper(axiosInstance.get(`MemberSearch/PacMedDetail?id=${planId}`)), //Active
      getPlanLogs: (planId: number) => axiosWrapper(axiosInstance.get(`MemberSearch/Logs?plan=${planId}`)), //Active
      updateReadInfo: (pacObj: Object) => axiosWrapper(axiosInstance.post(`MemberSearch/UpdatePacReadInfo`, pacObj)), //Active
      saveNote: (note: Object) => axiosWrapper(axiosInstance.post(`MemberSearch/PacNote`, note)), //Active
      savePlan: (plan: Object) => axiosWrapper(axiosInstance.post(`MemberSearch/PacPlan`, plan)), //Active
    },

    caseDoc: {
      getMembersAndCaseDocuments: (userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/MembersAndCaseDocuments?userId=${userId}`)), //Active
      getCaseByUserId: (userId: string, pageIndex: number, pageSize: number) => axiosWrapper(axiosInstance.get(`MemberSearch/GetCaseByUserId?userId=${userId}&pageIndex=${pageIndex}&pageSize=${pageSize}`)),
      getCaseLetterInfo: (caseNo: number, companyId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetCaseLetterInfo?caseNo=${caseNo}&companyId=${companyId}`)),
    },

    mcp: {
      saveTask: (task: SaveTask) => axiosWrapper(axiosInstance.post(`MemberSearch/McpTask`, task)), //Active
      deleteTask: (id: number, userId: string) => axiosWrapper(axiosInstance.delete(`MemberSearch/DelMcpTask?id=${id}&userId=${userId}`)), //Active
    },

    members: {
      getAlertCount: (pcpKeyId: string, companyId: string, timeSpan: string, vendorId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetMemberAlertCount?pcpKeyId=${pcpKeyId}&companyId=${companyId}&timeSpan=${timeSpan}&vendorId=${vendorId}`)), //Active
      getForAlertsRow1: (pcpKeyId: string, companyId: string, timeSpan: string, pageIndex: number, pageSize: number, vendorId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetMembersForAlertsRow1?pcpKeyId=${pcpKeyId}&companyId=${companyId}&timeSpan=${timeSpan}&pageIndex=${pageIndex}&pageSize=${pageSize}&vendorId=${vendorId}`)), //Active
      getForAlertsRow2: (pcpKeyId: string, companyId: string, timeSpan: number, pageIndex: number, pageSize: number) => axiosWrapper(axiosInstance.get(`MemberSearch/GetMembersForAlertsRow2?pcpKeyId=${pcpKeyId}&companyId=${companyId}&timeSpan=${timeSpan}&pageIndex=${pageIndex}&pageSize=${pageSize}`)),
    },
  },

  powerBI: {
    getAccessToken: () =>
      axiosWrapper(axiosInstance.get(`PowerBI/GetEmbedToken`)), //Active
    getProviders: (searchModel: Object) =>
      axiosWrapper(axiosInstance.post(`PowerBI/GetProviders`, searchModel)), //Active
  },

  hmo: {
    getMemberAwvDetails: (searchModel: HmoMemberAwvDetailsModel) =>
      axiosWrapper(axiosInstance.post(`Awv/MemberDetails`, searchModel)), //Active
    getMemberMpi: (searchModel: MemberMpiSearchModel) =>
      axiosWrapper(axiosInstance.post(`Awv/GetMemberMpi`, searchModel)), //Active
    getHmoAwvMetrics: (searchModel: HmoAwvMetricsModel) =>
      axiosWrapper(axiosInstance.post(`Awv/Metrics`, searchModel)), //Active
    getAcoAwvMetrics: (searchModel: HmoAwvMetricsModel) =>
      axiosWrapper(axiosInstance.post(`Awv/AcoMetrics`, searchModel)), //Active
    updateMemberAwvStatus: (updateModel: MemberBatchUpdateModel) =>
      axiosWrapper(
        axiosInstance.post("Awv/UpdateMemberAwvStatus", updateModel)
      ), //Active
    refreshMemberAwvStatus: (payload: RefreshMemberAwvStatusModel) =>
      axiosWrapper(axiosInstance.post("Awv/RefreshMemberAwvStatus", payload)), //Active
    downloadMemberPafs: (payload: MemberPafModel) =>
      axiosWrapper(
        axiosInstance.post("Awv/DownloadMemberPafs", payload, {
          responseType: "arraybuffer",
          headers: {
            Accept:
              "application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        })
      ), //Active
    downloadAwvStatusExcel: (payload: MemberExcelStatusModel) =>
      axiosWrapper(
        axiosInstance.post("Awv/DownloadAwvStatusExcel", payload, {
          responseType: "arraybuffer",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        })
      ), //Active
      downloadAwvStatusPdf: (payload: MemberExcelStatusModel) =>
        axiosWrapper(
          axiosInstance.post("Awv/DownloadAwvStatusPdf", payload, {
            responseType: "arraybuffer",
            headers: {
              Accept:
                "application/pdf",
            },
          })
        ), //Active
  },

  measure: {
    getMedicareMetricsOverview: (userId: string, pcpKeyId: string, ipaCode: string, measureYear: number, vendorId: string) => axiosWrapper(axiosInstance.get(`measure/getMedicareMetricsOverview?userId=${userId}&pcpKeyId=${pcpKeyId}&ipaCode=${ipaCode}&measureYear=${measureYear}&vendorId=${vendorId}`)), //Active
    saveMemberMeasure: (updateModel: Object) => axiosWrapper(axiosInstance.post(`MemberSearch/SaveMemberMeasures`, updateModel)), //Active
    getEmCodes: (updateModel: Object) => axiosWrapper(axiosInstance.post(`measure/getHedisServiceProc`, updateModel)), //Active
  },

  metrics: {
    getInpatientMetrics: (companyId: string, provKeyId: string, measureYear: number, userId: string) => axiosWrapper(axiosInstance.get(`MemberSearch/GetImpatientMetrics?companyId=${companyId}&provKeyId=${provKeyId}&measureYear=${measureYear}&userId=${userId}`)),
  }
};

export { api, axiosInstance, multiPartInstance, umsAxios };

export default api;
